import React, { Component, ReactNode } from 'react';
import { useGlobalProducts } from './global-stores';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  recoveryAttempted: boolean;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    recoveryAttempted: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { 
      hasError: true, 
      error,
      recoveryAttempted: false
    };
  }

  public componentDidCatch(error: Error, errorInfo: { componentStack: string }): void {
    console.error('Global error boundary caught error:', error, errorInfo);
    
    // Attempt state recovery if not already attempted
    if (!this.state.recoveryAttempted) {
      this.setState({ recoveryAttempted: true });
      this.attemptRecovery();
    }
  }

  private async attemptRecovery(): Promise<void> {
    try {
      // Clear any potentially corrupted state
      await useGlobalProducts.getState().recover();
      
      // Clear browser history state if it exists
      if (window.history.state) {
        window.history.replaceState(null, '');
      }
      
      // Force a clean reload after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Recovery attempt failed:', err);
    }
  }

  public render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
          <div className="p-6 max-w-sm mx-auto bg-white rounded-xl shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 mb-4">
              We're working to fix this issue. The page will reload automatically.
            </p>
            <div className="text-sm text-gray-500">
              {this.state.error?.message || 'An unexpected error occurred'}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
} 